/**
 * act-as-cdn-security.ts — React Query hooks for CDN security + optimization + operations.
 *
 * Covers: waf, firewall-rules, rate-limiting, access-control, cors, security-headers,
 *         verified-bots, bot-management, waf-analytics, bot-management-analytics,
 *         compression, image-optimization, timeouts, logging, operations (bindings),
 *         report-recipients.
 *
 * Query key factory: actAsCDNSecurityKeys.*
 * All mutations use actAsClient → X-Act-As-Org injected from session.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { actAsClient } from '@/api/act-as-client';

/* ── Shared envelope unwrap ──────────────────────────────────────────────── */

function unwrap<T>(body: unknown): T | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if ('data' in b && b.data !== null && b.data !== undefined) return b.data as T;
  return b as unknown as T;
}

function unwrapArray<T>(body: unknown, key?: string): T[] {
  const inner = unwrap<unknown>(body);
  if (Array.isArray(inner)) return inner as T[];
  if (inner && typeof inner === 'object') {
    const obj = inner as Record<string, unknown>;
    if (key && Array.isArray(obj[key])) return obj[key] as T[];
    const first = Object.values(obj).find(Array.isArray);
    if (first) return first as T[];
  }
  return [];
}

/* ── Types: WAF ──────────────────────────────────────────────────────────── */

export interface WafRuleSet {
  id: string;
  name: string;
  enabled: boolean;
  version?: string;
}

export interface WafConfig {
  enabled?: boolean;
  mode?: 'detect' | 'prevent';
  rule_sets?: WafRuleSet[];
  sensitivity?: 'low' | 'medium' | 'high';
  exclusions?: string[];
}

/* ── Types: Firewall Rules ───────────────────────────────────────────────── */

export interface FirewallCondition {
  field: string;
  operator: string;
  value: string;
}

export interface FirewallRule {
  id?: string;
  name: string;
  priority: number;
  enabled: boolean;
  action: 'allow' | 'block' | 'challenge' | 'log';
  conditions: FirewallCondition[];
}

export interface FirewallRulesConfig {
  rules: FirewallRule[];
}

/* ── Types: Rate Limiting ────────────────────────────────────────────────── */

export interface RateLimitRule {
  id?: string;
  name: string;
  enabled: boolean;
  match_path?: string;
  threshold: number;
  window_secs: number;
  action: 'block' | 'challenge' | 'log';
  response_code?: number;
}

export interface RateLimitingConfig {
  rules: RateLimitRule[];
}

/* ── Types: Access Control ───────────────────────────────────────────────── */

export interface AccessControlList {
  ips?: string[];
  cidrs?: string[];
  countries?: string[];
  asns?: string[];
}

export interface AccessControlConfig {
  enabled?: boolean;
  mode?: 'allowlist' | 'blocklist';
  allowlist?: AccessControlList;
  blocklist?: AccessControlList;
}

/* ── Types: CORS ─────────────────────────────────────────────────────────── */

export interface CorsConfig {
  enabled?: boolean;
  allow_origins?: string[];
  allow_methods?: string[];
  allow_headers?: string[];
  expose_headers?: string[];
  allow_credentials?: boolean;
  max_age_secs?: number;
}

/* ── Types: Security Headers ─────────────────────────────────────────────── */

export interface SecurityHeadersConfig {
  hsts_enabled?: boolean;
  hsts_max_age?: number;
  hsts_subdomains?: boolean;
  hsts_preload?: boolean;
  csp_enabled?: boolean;
  csp_policy?: string;
  x_frame_options?: 'DENY' | 'SAMEORIGIN' | 'disabled';
  x_content_type_options?: boolean;
  x_xss_protection?: boolean;
  referrer_policy?: string;
  permissions_policy?: string;
}

/* ── Types: Verified Bots ────────────────────────────────────────────────── */

export interface VerifiedBot {
  id: string;
  name: string;
  enabled: boolean;
  description?: string;
}

export interface VerifiedBotsConfig {
  bots: VerifiedBot[];
}

/* ── Types: Bot Management ───────────────────────────────────────────────── */

export interface BotManagementConfig {
  enabled?: boolean;
  mode?: 'monitor' | 'challenge' | 'block';
  challenge_score_threshold?: number;
  enable_js_fingerprinting?: boolean;
  enable_browser_integrity?: boolean;
  custom_rules?: BotCustomRule[];
}

export interface BotCustomRule {
  id?: string;
  name: string;
  enabled: boolean;
  min_score: number;
  action: 'allow' | 'challenge' | 'block' | 'log';
}

/* ── Types: Analytics ────────────────────────────────────────────────────── */

export interface AnalyticsDataPoint {
  ts: string;
  value: number;
}

export interface AnalyticsTopEvent {
  label: string;
  count: number;
  pct?: number;
}

export interface WafAnalyticsData {
  total_requests?: number;
  blocked_requests?: number;
  threats_detected?: number;
  requests_series?: AnalyticsDataPoint[];
  blocked_series?: AnalyticsDataPoint[];
  top_threats?: AnalyticsTopEvent[];
  top_blocked_ips?: AnalyticsTopEvent[];
  top_rule_ids?: AnalyticsTopEvent[];
}

export interface BotAnalyticsData {
  total_traffic?: number;
  bot_traffic?: number;
  blocked_bots?: number;
  traffic_series?: AnalyticsDataPoint[];
  bot_series?: AnalyticsDataPoint[];
  top_bot_types?: AnalyticsTopEvent[];
  top_blocked_agents?: AnalyticsTopEvent[];
}

/* ── Types: Optimization ─────────────────────────────────────────────────── */

export interface CompressionConfig {
  enabled?: boolean;
  algorithms?: string[];
  minimum_size_bytes?: number;
  content_type_filter?: string[];
}

export interface ImageOptimizationRule {
  id?: string;
  name: string;
  priority: number;
  match_type: string;
  match_path: string;
  output_format: 'original' | 'webp' | 'avif' | 'jpeg' | 'png';
  quality: number;
  max_width?: number;
  max_height?: number;
  match_extensions?: string[];
}

export interface ImageOptimizationConfig {
  enabled?: boolean;
  rules?: ImageOptimizationRule[];
}

export interface TimeoutsConfig {
  client_read_secs?: number;
  client_write_secs?: number;
  server_connect_secs?: number;
  server_read_secs?: number;
  keepalive_secs?: number;
  websocket_idle_secs?: number;
}

/* ── Types: Logging ──────────────────────────────────────────────────────── */

export interface LoggingConfig {
  access_log_enabled?: boolean;
  sampling_rate?: number;
}

/* ── Types: Operations / Bindings ────────────────────────────────────────── */

export interface CdnBinding {
  id: string;
  provider: string;
  uid?: string;
  cname?: string;
  status?: string;
  live_status?: string;
  enabled?: boolean;
  created_at?: string;
}

export interface OperationsConfig {
  bindings?: CdnBinding[];
  logging?: LoggingConfig;
}

export interface BindingLiveStatus {
  bindings: Record<string, { status: string; latency_ms?: number }>;
}

export interface ProvisionBindingRequest {
  provider?: string;
  acknowledge_cost?: boolean;
}

export interface JobGroupStatus {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: number;
  message?: string;
}

/* ── Types: Report Recipients ────────────────────────────────────────────── */

export interface ReportRecipient {
  id: string;
  email: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  status?: string;
  created_at?: string;
}

export interface AddRecipientRequest {
  email: string;
  frequency: 'daily' | 'weekly' | 'monthly';
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const actAsCDNSecurityKeys = {
  waf: (d: string) => ['act-as', 'cdn', 'domain', d, 'waf'] as const,
  firewallRules: (d: string) => ['act-as', 'cdn', 'domain', d, 'firewall-rules'] as const,
  rateLimiting: (d: string) => ['act-as', 'cdn', 'domain', d, 'rate-limiting'] as const,
  accessControl: (d: string) => ['act-as', 'cdn', 'domain', d, 'access-control'] as const,
  cors: (d: string) => ['act-as', 'cdn', 'domain', d, 'cors'] as const,
  securityHeaders: (d: string) => ['act-as', 'cdn', 'domain', d, 'security-headers'] as const,
  verifiedBots: (d: string) => ['act-as', 'cdn', 'domain', d, 'verified-bots'] as const,
  botManagement: (d: string) => ['act-as', 'cdn', 'domain', d, 'bot-management'] as const,
  wafAnalytics: (d: string, from?: string, to?: string) =>
    ['act-as', 'cdn', 'domain', d, 'waf-analytics', from, to] as const,
  botAnalytics: (d: string, from?: string, to?: string) =>
    ['act-as', 'cdn', 'domain', d, 'bot-analytics', from, to] as const,
  compression: (d: string) => ['act-as', 'cdn', 'domain', d, 'compression'] as const,
  imageOptimization: (d: string) =>
    ['act-as', 'cdn', 'domain', d, 'image-optimization'] as const,
  timeouts: (d: string) => ['act-as', 'cdn', 'domain', d, 'timeouts'] as const,
  logging: (d: string) => ['act-as', 'cdn', 'domain', d, 'logging'] as const,
  operations: (d: string) => ['act-as', 'cdn', 'domain', d, 'operations'] as const,
  bindingLiveStatus: (d: string) =>
    ['act-as', 'cdn', 'domain', d, 'bindings', 'live-status'] as const,
  reportRecipients: (d: string) =>
    ['act-as', 'cdn', 'domain', d, 'report-recipients'] as const,
  jobGroup: (groupId: string) => ['act-as', 'jobs', 'groups', groupId] as const,
};

/* ── GET hooks — security ────────────────────────────────────────────────── */

function makeGetHook<T>(path: (d: string) => string, key: (d: string) => readonly unknown[]) {
  return function useHook(domainId: string | undefined, enabled = true) {
    return useQuery({
      queryKey: key(domainId ?? ''),
      enabled: Boolean(domainId) && enabled,
      staleTime: 30_000,
      queryFn: async (): Promise<T | null> => {
        try {
          const res = await actAsClient.get<unknown>(path(domainId!));
          return unwrap<T>(res.data);
        } catch (e) {
          const status = (e as { response?: { status?: number } })?.response?.status;
          if (status === 404 || status === 400) return null;
          throw e;
        }
      },
    });
  };
}

export const useActAsCDNWaf = makeGetHook<WafConfig>(
  (d) => `/cdn/domains/${d}/waf`,
  actAsCDNSecurityKeys.waf,
);

export const useActAsCDNFirewallRules = makeGetHook<FirewallRulesConfig>(
  (d) => `/cdn/domains/${d}/firewall-rules`,
  actAsCDNSecurityKeys.firewallRules,
);

export const useActAsCDNRateLimiting = makeGetHook<RateLimitingConfig>(
  (d) => `/cdn/domains/${d}/rate-limiting`,
  actAsCDNSecurityKeys.rateLimiting,
);

export const useActAsCDNAccessControl = makeGetHook<AccessControlConfig>(
  (d) => `/cdn/domains/${d}/access-control`,
  actAsCDNSecurityKeys.accessControl,
);

export const useActAsCDNCors = makeGetHook<CorsConfig>(
  (d) => `/cdn/domains/${d}/cors`,
  actAsCDNSecurityKeys.cors,
);

export const useActAsCDNSecurityHeaders = makeGetHook<SecurityHeadersConfig>(
  (d) => `/cdn/domains/${d}/security-headers`,
  actAsCDNSecurityKeys.securityHeaders,
);

export const useActAsCDNVerifiedBots = makeGetHook<VerifiedBotsConfig>(
  (d) => `/cdn/domains/${d}/verified-bots`,
  actAsCDNSecurityKeys.verifiedBots,
);

export const useActAsCDNBotManagement = makeGetHook<BotManagementConfig>(
  (d) => `/cdn/domains/${d}/bot-management`,
  actAsCDNSecurityKeys.botManagement,
);

export const useActAsCDNCompression = makeGetHook<CompressionConfig>(
  (d) => `/cdn/domains/${d}/compression`,
  actAsCDNSecurityKeys.compression,
);

export const useActAsCDNImageOptimization = makeGetHook<ImageOptimizationConfig>(
  (d) => `/cdn/domains/${d}/image-optimization`,
  actAsCDNSecurityKeys.imageOptimization,
);

export const useActAsCDNTimeouts = makeGetHook<TimeoutsConfig>(
  (d) => `/cdn/domains/${d}/timeouts`,
  actAsCDNSecurityKeys.timeouts,
);

export const useActAsCDNLogging = makeGetHook<LoggingConfig>(
  (d) => `/cdn/domains/${d}/logging`,
  actAsCDNSecurityKeys.logging,
);

export const useActAsCDNOperations = makeGetHook<OperationsConfig>(
  (d) => `/cdn/domains/${d}/operations`,
  actAsCDNSecurityKeys.operations,
);

/* ── Analytics hooks (read-only, param-keyed) ────────────────────────────── */

export function useActAsCDNWafAnalytics(
  domainId: string | undefined,
  from?: string,
  to?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: actAsCDNSecurityKeys.wafAnalytics(domainId ?? '', from, to),
    enabled: Boolean(domainId) && enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<WafAnalyticsData | null> => {
      try {
        const params: Record<string, string> = {};
        if (from) params.from = from;
        if (to) params.to = to;
        const res = await actAsClient.get<unknown>(
          `/cdn/domains/${domainId}/waf-analytics`,
          { params },
        );
        return unwrap<WafAnalyticsData>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

export function useActAsCDNBotAnalytics(
  domainId: string | undefined,
  from?: string,
  to?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: actAsCDNSecurityKeys.botAnalytics(domainId ?? '', from, to),
    enabled: Boolean(domainId) && enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<BotAnalyticsData | null> => {
      try {
        const params: Record<string, string> = {};
        if (from) params.from = from;
        if (to) params.to = to;
        const res = await actAsClient.get<unknown>(
          `/cdn/domains/${domainId}/bot-management-analytics`,
          { params },
        );
        return unwrap<BotAnalyticsData>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return null;
        throw e;
      }
    },
  });
}

/* ── Binding live-status hook (polling) ──────────────────────────────────── */

export function useActAsCDNBindingLiveStatus(
  domainId: string,
  enabled = false,
) {
  return useQuery({
    queryKey: actAsCDNSecurityKeys.bindingLiveStatus(domainId),
    enabled: Boolean(domainId) && enabled,
    refetchInterval: 10_000,
    staleTime: 0,
    queryFn: async (): Promise<BindingLiveStatus | null> => {
      try {
        const res = await actAsClient.get<unknown>(
          `/cdn/domains/${domainId}/bindings/live-status`,
        );
        return unwrap<BindingLiveStatus>(res.data);
      } catch {
        return null;
      }
    },
  });
}

/* ── Job group polling hook ──────────────────────────────────────────────── */

export function useActAsCDNJobGroup(domainId: string, groupId: string | null) {
  return useQuery({
    queryKey: actAsCDNSecurityKeys.jobGroup(groupId ?? ''),
    enabled: Boolean(groupId),
    refetchInterval: (q) => {
      const d = q.state.data as JobGroupStatus | null;
      if (!d) return 3_000;
      return d.status === 'completed' || d.status === 'failed' ? false : 3_000;
    },
    queryFn: async (): Promise<JobGroupStatus | null> => {
      const res = await actAsClient.get<unknown>(
        `/cdn/domains/${domainId}/operations/job-group/${groupId}`,
      );
      return unwrap<JobGroupStatus>(res.data);
    },
  });
}

/* ── Report recipients hook ──────────────────────────────────────────────── */

export function useActAsCDNReportRecipients(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNSecurityKeys.reportRecipients(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<ReportRecipient[]> => {
      try {
        const res = await actAsClient.get<unknown>(
          `/cdn/domains/${domainId}/report-recipients`,
        );
        return unwrapArray<ReportRecipient>(res.data, 'recipients');
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) return [];
        throw e;
      }
    },
  });
}

/* ── Security mutations ──────────────────────────────────────────────────── */

function makePutMutation<T>(path: (d: string) => string, key: (d: string) => readonly unknown[]) {
  return function usePutHook(domainId: string) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (payload: T) => {
        const res = await actAsClient.put<unknown>(path(domainId), payload);
        return res.data;
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: key(domainId) }),
    });
  };
}

export const useUpdateActAsCDNWaf = makePutMutation<WafConfig>(
  (d) => `/cdn/domains/${d}/waf`,
  actAsCDNSecurityKeys.waf,
);

export const useUpdateActAsCDNFirewallRules = makePutMutation<FirewallRulesConfig>(
  (d) => `/cdn/domains/${d}/firewall-rules`,
  actAsCDNSecurityKeys.firewallRules,
);

export const useUpdateActAsCDNRateLimiting = makePutMutation<RateLimitingConfig>(
  (d) => `/cdn/domains/${d}/rate-limiting`,
  actAsCDNSecurityKeys.rateLimiting,
);

export const useUpdateActAsCDNAccessControl = makePutMutation<AccessControlConfig>(
  (d) => `/cdn/domains/${d}/access-control`,
  actAsCDNSecurityKeys.accessControl,
);

export const useUpdateActAsCDNCors = makePutMutation<CorsConfig>(
  (d) => `/cdn/domains/${d}/cors`,
  actAsCDNSecurityKeys.cors,
);

export const useUpdateActAsCDNSecurityHeaders = makePutMutation<SecurityHeadersConfig>(
  (d) => `/cdn/domains/${d}/security-headers`,
  actAsCDNSecurityKeys.securityHeaders,
);

export const useUpdateActAsCDNVerifiedBots = makePutMutation<VerifiedBotsConfig>(
  (d) => `/cdn/domains/${d}/verified-bots`,
  actAsCDNSecurityKeys.verifiedBots,
);

export const useUpdateActAsCDNBotManagement = makePutMutation<BotManagementConfig>(
  (d) => `/cdn/domains/${d}/bot-management`,
  actAsCDNSecurityKeys.botManagement,
);

export const useUpdateActAsCDNCompression = makePutMutation<CompressionConfig>(
  (d) => `/cdn/domains/${d}/compression`,
  actAsCDNSecurityKeys.compression,
);

export const useUpdateActAsCDNImageOptimization = makePutMutation<ImageOptimizationConfig>(
  (d) => `/cdn/domains/${d}/image-optimization`,
  actAsCDNSecurityKeys.imageOptimization,
);

export const useUpdateActAsCDNTimeouts = makePutMutation<TimeoutsConfig>(
  (d) => `/cdn/domains/${d}/timeouts`,
  actAsCDNSecurityKeys.timeouts,
);

export const useUpdateActAsCDNLogging = makePutMutation<LoggingConfig>(
  (d) => `/cdn/domains/${d}/logging`,
  actAsCDNSecurityKeys.logging,
);

/* ── Binding mutations ───────────────────────────────────────────────────── */

/** POST /cdn/domains/:d/bindings/provision */
export function useProvisionActAsCDNBinding(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: ProvisionBindingRequest | undefined): Promise<{ group_id?: string }> => {
      const res = await actAsClient.post<unknown>(
        `/cdn/domains/${domainId}/bindings/provision`,
        req,
      );
      return unwrap<{ group_id?: string }>(res.data) ?? {};
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNSecurityKeys.operations(domainId) }),
  });
}

/** POST /cdn/domains/:d/bindings/:bindingId/enable|disable|delete */
export function useActAsCDNBindingAction(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bindingId, action }: { bindingId: string; action: 'enable' | 'disable' | 'delete' }) => {
      await actAsClient.post(`/cdn/domains/${domainId}/bindings/${bindingId}/${action}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: actAsCDNSecurityKeys.operations(domainId) }),
  });
}

/* ── Report recipient mutations ──────────────────────────────────────────── */

/** POST /cdn/domains/:d/report-recipients */
export function useAddActAsCDNReportRecipient(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: AddRecipientRequest) => {
      await actAsClient.post(`/cdn/domains/${domainId}/report-recipients`, req);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: actAsCDNSecurityKeys.reportRecipients(domainId) }),
  });
}

/** DELETE /cdn/domains/:d/report-recipients/:id */
export function useDeleteActAsCDNReportRecipient(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (recipientId: string) => {
      await actAsClient.delete(`/cdn/domains/${domainId}/report-recipients/${recipientId}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: actAsCDNSecurityKeys.reportRecipients(domainId) }),
  });
}
